"use client";

// ── The result card, floating over the lower editor (`EX-EDR01`) ────────────
// Wave 23 decision 1: details never take height from the editor. The card
// opens from the pill or the strip's Details, sits over the bottom of the
// editor, and closes on Escape, on its X, or as soon as the reader types
// (the caller closes it on an edit, because only the caller sees the draft).
// It is not a modal: the page behind it stays live and nothing is trapped.

import { useEffect, type ReactNode } from "react";
import Link from "next/link";
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2, X, XCircle } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import { submissionCompileFeedback } from "./compile-diagnostics";
import { Button, buttonVariants } from "@/components/ui/button";
import { formatRuntimeLanguageName } from "@/features/quiz/lib/coding-task-format";
import { verdictTone } from "../../lib/format";
import { runtimeLabel } from "./EditorToolbarControls";
import { formatMegabytes } from "./TestStrip";
import { RunTwiceStageLine, RunTwiceTestTable } from "./RunTwiceResult";
import { hasRunTwiceRows, runTwiceSummaryOf } from "../../lib/run-twice";
import {
  isCompileError,
  longestTimeMs,
  mostMemoryKb,
  testCounts,
  type WorkspaceSubmissionView,
} from "./workspace-result";

export type ResultCardLink = { label: string } & ({ href: string } | { onClick: () => void });

export function ResultCard({
  id,
  view,
  subtitle,
  countOnly,
  compileNote,
  link,
  onClose,
  locale,
  children,
  onGoToLine,
}: {
  id: string;
  view: WorkspaceSubmissionView;
  /** "B · Attempt 2 · 1 Oct 2026, 22:54 Bangladesh time", built by the caller. */
  subtitle: string;
  countOnly: boolean;
  /** The surface's own sentence for a compilation error. */
  compileNote: string;
  link: ResultCardLink | null;
  onClose: () => void;
  locale: string;
  /** Surface-specific rows under the figures (the rehearsal's runs list). */
  children?: ReactNode;
  onGoToLine?: (line: number, column: number) => void;
}) {
  const t = useTranslations("judge");
  const tEditor = useTranslations("judge.editor");

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pending = view.status !== "completed";
  const compile = isCompileError(view);
  const feedback = submissionCompileFeedback(view);
  const firstError = feedback.errors[0];
  const counts = testCounts(view);
  const tone = pending ? "pending" : compile ? "warning" : verdictTone(view.verdict);
  const timeMs = longestTimeMs(view);
  const memoryKb = mostMemoryKb(view);
  // `EX-RTW03`: `null` on a standard problem, so the card is what it was.
  const runTwice = !pending && !compile ? runTwiceSummaryOf(view) : null;

  const title = pending
    ? view.status === "queued"
      ? tEditor("pillQueued")
      : tEditor("pillJudging")
    : t(`verdict.${view.verdict ?? "internal_error"}`);

  const figures: { label: string; value: string }[] = [
    ...(timeMs != null ? [{ label: tEditor("cardLongestTime"), value: tEditor("figureMs", { ms: timeMs }) }] : []),
    ...(memoryKb != null
      ? [{ label: tEditor("cardMostMemory"), value: tEditor("figureMb", { mb: formatMegabytes(memoryKb, locale) }) }]
      : []),
    ...(view.languageKey
      ? [
          {
            label: tEditor("cardLanguage"),
            value: view.languageVersion
              ? runtimeLabel(view.languageKey, view.languageVersion)
              : formatRuntimeLanguageName(view.languageKey),
          },
        ]
      : []),
  ];

  return (
    <section
      id={id}
      aria-label={tEditor("cardLabel")}
      data-testid="result-card"
      className="absolute inset-x-3 bottom-3 z-20 max-h-[calc(100%-1.5rem)] overflow-y-auto rounded-xl border border-white/10 bg-neutral-900 p-4 text-white/90 shadow-card sm:left-auto sm:w-[26rem]"
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
            tone === "pending" && "bg-white/10 text-white/70",
            tone === "success" && "bg-emerald-400/15 text-emerald-300",
            tone === "warning" && "bg-amber-400/15 text-amber-300",
            tone === "error" && "bg-red-400/15 text-red-300",
            tone === "neutral" && "bg-white/10 text-white/70",
          )}
          aria-hidden
        >
          {tone === "pending" ? (
            <Loader2 className="h-5 w-5 motion-safe:animate-spin" />
          ) : tone === "warning" ? (
            <AlertTriangle className="h-5 w-5" />
          ) : tone === "success" ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <XCircle className="h-5 w-5" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-lg font-bold leading-tight text-white">{title}</p>
          <p className="mt-0.5 text-xs text-white/55">{subtitle}</p>
        </div>
        <Button
          type="button"
          variant="ghostDark"
          size="smIcon"
          onClick={onClose}
          aria-label={tEditor("cardClose")}
          className="shrink-0"
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      {compile ? (
        <>
          <p className="mt-3 rounded-lg border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
            {compileNote}
          </p>
          {feedback.output ? (
            <>
              <p className="mt-3 text-xs font-semibold text-white/55">{tEditor("cardCompilerMessage")}</p>
              <pre className="mt-1.5 max-h-32 overflow-auto whitespace-pre-wrap rounded-lg bg-black/40 p-2.5 text-[11px] text-red-300">
                {feedback.output}
              </pre>
              {firstError && onGoToLine ? (
                <Button type="button" variant="linkDark" size="sm" onClick={() => onGoToLine(firstError.line, firstError.column)}>
                  {tEditor("goToLine", { n: firstError.line })}
                </Button>
              ) : null}
            </>
          ) : null}
        </>
      ) : counts ? (
        <div className="mt-4">
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-white/55">{tEditor("cardTestsPassed")}</span>
            <span className="font-semibold tabular-nums" data-testid="result-card-tests">
              {tEditor("cardTestsValue", { passed: counts.passed, total: counts.total })}
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/10">
            <div
              className={cn("h-full rounded-full", counts.passed === counts.total ? "bg-emerald-400" : "bg-red-400")}
              style={{ width: `${counts.total > 0 ? (counts.passed / counts.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      ) : null}

      {figures.length > 0 && !pending ? (
        <dl className="mt-3 grid grid-cols-3 gap-2">
          {figures.map((figure) => (
            <div key={figure.label} className="min-w-0 rounded-lg bg-white/5 px-3 py-2">
              <dt className="truncate text-[11px] text-white/50">{figure.label}</dt>
              <dd className="truncate text-sm font-semibold tabular-nums">{figure.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {runTwice ? <RunTwiceStageLine summary={runTwice} tone="dark" locale={locale} /> : null}
      {runTwice && view.testResults && hasRunTwiceRows(view.testResults) ? (
        <RunTwiceTestTable rows={view.testResults} tone="dark" locale={locale} />
      ) : null}

      {countOnly && counts ? (
        <p className="mt-3 text-xs leading-relaxed text-white/50">{tEditor("cardCountOnlyNote")}</p>
      ) : null}

      {children}

      {link ? (
        <div className="mt-3 flex justify-end border-t border-white/10 pt-2">
          {"href" in link ? (
            <Link href={link.href} className={buttonVariants({ variant: "linkDark", size: "sm" })}>
              {link.label}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          ) : (
            <Button type="button" variant="linkDark" size="sm" onClick={link.onClick}>
              {link.label}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Button>
          )}
        </div>
      ) : null}
    </section>
  );
}
